import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, TextField, Grid,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Chip,
  Stack, FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Tooltip, LinearProgress, Paper, Avatar
} from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterListIcon from '@mui/icons-material/FilterList';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import WorkIcon from '@mui/icons-material/Work';
import PeopleIcon from '@mui/icons-material/People';
import API_ENDPOINTS from '../config/apiConfig';
import FixedSnackbar from './FixedSnackbar';
import { useTranslation } from '../i18n/i18n';
import LoadingButton from './LoadingButton';
import './Hirees.css';

function Vacancies() {
  const { t } = useTranslation();
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Vacancy form
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [editingVacancy, setEditingVacancy] = useState(null);
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formStatus, setFormStatus] = useState('open');
  const [formAtsKeywords, setFormAtsKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [suggestingKeywords, setSuggestingKeywords] = useState(false);
  const [savingVacancy, setSavingVacancy] = useState(false);
  const [formPosition, setFormPosition] = useState('');
  const [positions, setPositions] = useState([]);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Selected vacancy + prospects
  const [selectedVacancy, setSelectedVacancy] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [loadingProspects, setLoadingProspects] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [resumePreviewOpen, setResumePreviewOpen] = useState(false);
  const [promotingId, setPromotingId] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const cardsPerRow = 3;
  const detailsRef = useRef(null);

  // Auto-scroll to prospects panel when a vacancy is selected
  useEffect(() => {
    if (selectedVacancy && detailsRef.current) {
      setTimeout(() => {
        detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
  }, [selectedVacancy]);

  const getBubbleIndex = () => {
    if (!selectedVacancy) return -1;
    return filteredVacancies.findIndex((v) => v._id === selectedVacancy._id);
  };

  const fetchVacancies = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(API_ENDPOINTS.VACANCIES, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setVacancies(Array.isArray(data) ? data : []);
    } catch {
      setSnackbar({ open: true, message: t('vacancy_error_fetch'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchVacancies(); }, [fetchVacancies]);

  const fetchProspects = async (vacancyId) => {
    setLoadingProspects(true);
    try {
      const res = await fetch(API_ENDPOINTS.VACANCY_PROSPECTS(vacancyId), { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setProspects(Array.isArray(data) ? data : []);
    } catch {
      setProspects([]);
    } finally {
      setLoadingProspects(false);
    }
  };

  const handleSelectVacancy = (vacancy) => {
    if (selectedVacancy?._id === vacancy._id) {
      setSelectedVacancy(null);
      setProspects([]);
      setSelectedProspect(null);
    } else {
      setSelectedVacancy(vacancy);
      setSelectedProspect(null);
      fetchProspects(vacancy._id);
    }
  };

  const fetchPositions = async () => {
    try {
      const res = await fetch(API_ENDPOINTS.POSITIONS, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPositions(Array.isArray(data) ? data : []);
      }
    } catch { /* silent */ }
  };

  const openCreateDialog = () => {
    setEditingVacancy(null);
    setFormTitle('');
    setFormDescription('');
    setFormPosition('');
    setFormStatus('open');
    setFormAtsKeywords([]);
    setNewKeyword('');
    fetchPositions();
    setOpenFormDialog(true);
  };

  const openEditDialog = (vacancy) => {
    setEditingVacancy(vacancy);
    setFormTitle(vacancy.title || '');
    setFormDescription(vacancy.description || '');
    setFormPosition(vacancy.position_id || positions.find(position => position.display_text === vacancy.position)?.id || '');
    setFormStatus(vacancy.status || 'open');
    setFormAtsKeywords(vacancy.ats_keywords || []);
    setNewKeyword('');
    fetchPositions();
    setOpenFormDialog(true);
  };

  const handleSaveVacancy = async () => {
    if (!formTitle.trim() || !formDescription.trim()) return;
    setSavingVacancy(true);
    try {
      const url = editingVacancy
        ? API_ENDPOINTS.VACANCY_DETAIL(editingVacancy._id)
        : API_ENDPOINTS.VACANCIES;
      const method = editingVacancy ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: formTitle.trim(), description: formDescription.trim(), position_id: formPosition, status: formStatus, ats_keywords: formAtsKeywords }),
      });
      if (res.ok) {
        setSnackbar({ open: true, message: editingVacancy ? t('vacancy_updated_success') : t('vacancy_created_success'), severity: 'success' });
        setOpenFormDialog(false);
        fetchVacancies();
      } else {
        setSnackbar({ open: true, message: editingVacancy ? t('vacancy_error_update') : t('vacancy_error_create'), severity: 'error' });
      }
    } catch {
      setSnackbar({ open: true, message: t('vacancy_error_create'), severity: 'error' });
    } finally {
      setSavingVacancy(false);
    }
  };

  const handleDeleteVacancy = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await fetch(API_ENDPOINTS.VACANCY_DETAIL(deleteConfirm._id), {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setSnackbar({ open: true, message: t('vacancy_deleted_success'), severity: 'success' });
        if (selectedVacancy?._id === deleteConfirm._id) {
          setSelectedVacancy(null);
          setProspects([]);
        }
        fetchVacancies();
      } else {
        setSnackbar({ open: true, message: t('vacancy_error_delete'), severity: 'error' });
      }
    } catch {
      setSnackbar({ open: true, message: t('vacancy_error_delete'), severity: 'error' });
    }
    setDeleteConfirm(null);
  };

  const handleCopyLink = (token) => {
    const url = `${window.location.origin}/apply/${token}`;
    navigator.clipboard.writeText(url).then(() => {
      setSnackbar({ open: true, message: t('vacancy_link_copied'), severity: 'success' });
    });
  };

  const handleSuggestKeywords = async () => {
    if (!formDescription.trim()) return;
    setSuggestingKeywords(true);
    try {
      const res = await fetch(API_ENDPOINTS.VACANCY_SUGGEST_KEYWORDS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ description: formDescription }),
      });
      if (res.ok) {
        const data = await res.json();
        // Merge with existing keywords (don't duplicate)
        const existing = new Set(formAtsKeywords.map(k => k.toLowerCase()));
        const newKeywords = (data.keywords || []).filter(k => !existing.has(k.toLowerCase()));
        setFormAtsKeywords([...formAtsKeywords, ...newKeywords]);
      }
    } catch { /* silent */ }
    setSuggestingKeywords(false);
  };

  const handleAddKeyword = () => {
    const kw = newKeyword.trim().toLowerCase();
    if (!kw) return;
    if (formAtsKeywords.some(k => k.toLowerCase() === kw)) return;
    setFormAtsKeywords([...formAtsKeywords, kw]);
    setNewKeyword('');
  };

  const handleRemoveKeyword = (kw) => {
    setFormAtsKeywords(formAtsKeywords.filter(k => k !== kw));
  };

  const handlePromoteProspect = async (prospectId) => {
    setPromotingId(prospectId);
    try {
      const res = await fetch(API_ENDPOINTS.PROSPECT_PROMOTE(prospectId), {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        setSnackbar({ open: true, message: t('prospect_promote_success'), severity: 'success' });
        if (selectedVacancy) fetchProspects(selectedVacancy._id);
        setSelectedProspect(null);
      } else {
        const data = await res.json();
        setSnackbar({ open: true, message: data.message_key ? t(data.message_key) : (data.message || t('prospect_promote_error')), severity: 'error' });
      }
    } catch {
      setSnackbar({ open: true, message: t('prospect_promote_error'), severity: 'error' });
    }
    setPromotingId(null);
  };

  const handleUpdateProspectStatus = async (prospectId, newStatus) => {
    try {
      const res = await fetch(API_ENDPOINTS.PROSPECT_STATUS(prospectId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        if (selectedVacancy) fetchProspects(selectedVacancy._id);
        if (selectedProspect?._id === prospectId) {
          setSelectedProspect(prev => ({ ...prev, status: newStatus }));
        }
      }
    } catch { /* silent */ }
  };

  const getStatusChip = (status) => {
    const map = {
      open: { label: t('vacancy_status_open'), color: 'success' },
      closed: { label: t('vacancy_status_closed'), color: 'error' },
    };
    const cfg = map[status] || map.open;
    return (
      <Chip
        label={cfg.label}
        size="small"
        color={cfg.color}
        sx={{ height: 24, fontSize: '0.75rem', fontWeight: 500 }}
      />
    );
  };

  const getProspectStatusChip = (status) => {
    const map = {
      new: { label: t('prospect_status_new'), color: 'info' },
      reviewed: { label: t('prospect_status_reviewed'), color: 'warning' },
      promoted: { label: t('prospect_status_promoted'), color: 'success' },
      rejected: { label: t('prospect_status_rejected'), color: 'error' },
    };
    const cfg = map[status] || map.new;
    return (
      <Chip
        label={cfg.label}
        size="small"
        color={cfg.color}
        sx={{ height: 24, fontSize: '0.75rem', fontWeight: 500 }}
      />
    );
  };

  const filteredVacancies = vacancies.filter(v =>
    !searchQuery || (v.title || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, sm: 4 } }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: { xs: 3, sm: 4 } }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {t('vacancies_panel')}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 640 }}>
            {t('vacancies_subtitle') || 'Manage job vacancies, review applicants, and promote qualified prospects.'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } }}
        >
          {t('vacancy_create')}
        </Button>
      </Box>

      {/* Search Bar */}
      <TextField
        size="small"
        variant="outlined"
        placeholder={t('search_modules') || 'Search vacancies...'}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <FilterListIcon fontSize="small" sx={{ color: '#62B6CB', mr: 0.5 }} />
          ),
        }}
        sx={{ mb: 3, minWidth: { xs: '100%', sm: 200 }, maxWidth: 350 }}
      />

      {/* Loading State */}
      {loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading vacancies...
          </Typography>
        </Box>
      )}

      {/* Empty State */}
      {!loading && filteredVacancies.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
          <Avatar sx={{ width: 64, height: 64, mb: 2, bgcolor: 'grey.100', color: 'grey.400' }}>
            <WorkIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {t('vacancy_no_vacancies')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create your first vacancy to start accepting applications.
          </Typography>
        </Box>
      )}

      {/* Vacancy Cards Grid */}
      {!loading && filteredVacancies.length > 0 && (
        <Grid container spacing={2}>
          {filteredVacancies.map((vacancy, idx) => {
            const isSelected = selectedVacancy?._id === vacancy._id;
            const isLastInRow = (idx + 1) % cardsPerRow === 0;
            const bubbleIndex = getBubbleIndex();
            const isBubbleRow =
              bubbleIndex !== -1 &&
              (Math.floor(bubbleIndex / cardsPerRow) === Math.floor(idx / cardsPerRow)) &&
              (isLastInRow || idx === filteredVacancies.length - 1);

            return (
              <React.Fragment key={vacancy._id}>
                <Grid item xs={12} sm={6} md={4} sx={{ position: 'relative' }}>
                  <div className={isSelected ? 'selected-card-wrapper' : ''}>
                    <Paper
                      elevation={0}
                      className="user-card"
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 2,
                        border: isSelected ? '2px solid' : '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        transition: 'all 0.15s ease-in-out',
                        '&:hover': { borderColor: 'primary.light', boxShadow: 1 },
                        position: 'relative',
                        zIndex: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 220
                      }}
                      onClick={() => handleSelectVacancy(vacancy)}
                    >
                      <Box sx={{ p: { xs: 2, sm: 2.5 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="h6" noWrap sx={{ flex: 1, fontWeight: 600 }}>
                            {vacancy.title}
                          </Typography>
                          {getStatusChip(vacancy.status)}
                        </Stack>
                        {vacancy.position && (
                          <Chip
                            label={vacancy.position}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ mb: 1, height: 24, fontSize: '0.75rem', alignSelf: 'flex-start' }}
                          />
                        )}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 1,
                            flex: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: 60
                          }}
                        >
                          {vacancy.description || ' '}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 'auto' }}>
                          {t('vacancy_prospects')}: {vacancy.prospect_count || 0}
                        </Typography>
                      </Box>
                      <Box sx={{ px: 2, pb: 2, pt: 0 }}>
                        <Tooltip title={t('vacancy_copy_link')}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleCopyLink(vacancy.token); }}
                            sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('edit')}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); openEditDialog(vacancy); }}
                            sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('delete')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirm(vacancy); }}
                            sx={{ '&:hover': { bgcolor: 'error.main' + '14' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Paper>
                    {isSelected && (
                      <>
                        <div className="vertical-line left" />
                        <div className="vertical-line right" />
                      </>
                    )}
                  </div>
                </Grid>

                {/* --- MOBILE: Prospects bubble immediately after selected card --- */}
                {isMobile && isSelected && (
                  <Grid item xs={12} sx={{ position: 'relative' }} ref={detailsRef}>
                    <Paper elevation={0} sx={{ position: 'relative', border: 2, borderColor: 'primary.main', borderRadius: 2, p: { xs: 2, sm: 3 }, mb: 2, mt: 0, zIndex: 10, minWidth: 280 }}>
                      <IconButton size="small" sx={{ position: 'absolute', top: 8, right: 8 }} onClick={() => { setSelectedVacancy(null); setProspects([]); setSelectedProspect(null); }}>
                        <CloseIcon />
                      </IconButton>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                          <PeopleIcon sx={{ fontSize: 20 }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {t('vacancy_prospects')} — {selectedVacancy.title}
                        </Typography>
                      </Box>

                      {loadingProspects && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <CircularProgress size={28} />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading prospects...</Typography>
                        </Box>
                      )}

                      {!loadingProspects && prospects.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">{t('vacancy_no_prospects')}</Typography>
                        </Box>
                      )}

                      {!loadingProspects && prospects.length > 0 && (
                        <Grid container spacing={2}>
                          {prospects.map((prospect) => (
                            <Grid item xs={12} sm={6} md={4} key={prospect._id}>
                              <Paper
                                elevation={0}
                                sx={{
                                  cursor: 'pointer', borderRadius: 2,
                                  border: selectedProspect?._id === prospect._id ? '2px solid' : '1px solid',
                                  borderColor: selectedProspect?._id === prospect._id ? 'secondary.main' : 'divider',
                                  transition: 'all 0.15s ease-in-out',
                                  '&:hover': { borderColor: 'secondary.light', boxShadow: 1 }, minHeight: 120
                                }}
                                onClick={() => setSelectedProspect(prospect)}
                              >
                                <Box sx={{ p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{prospect.fullname}</Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{prospect.email}</Typography>
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 'auto' }}>
                                    {getProspectStatusChip(prospect.status)}
                                    <Typography variant="caption" color="text.secondary">{t('prospect_ats_score')}: {prospect.ats_score}%</Typography>
                                  </Stack>
                                </Box>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      )}

                      {/* Prospect Detail (inline) */}
                      {selectedProspect && (
                        <Paper elevation={0} sx={{ mt: 3, p: { xs: 2, sm: 3 }, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>{selectedProspect.fullname}</Typography>
                              <Typography variant="body2" color="text.secondary">{selectedProspect.email}</Typography>
                            </Box>
                            <IconButton size="small" onClick={() => setSelectedProspect(null)}><CloseIcon /></IconButton>
                          </Box>
                          <Box sx={{ mt: 2, mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{t('prospect_ats_score')}</Typography>
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <LinearProgress variant="determinate" value={selectedProspect.ats_score || 0} sx={{ flex: 1, height: 10, borderRadius: 5 }} color={selectedProspect.ats_score >= 50 ? 'success' : selectedProspect.ats_score >= 25 ? 'warning' : 'error'} />
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedProspect.ats_score || 0}%</Typography>
                            </Stack>
                          </Box>
                          {selectedProspect.ats_matched_keywords?.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{t('prospect_matched_keywords')}</Typography>
                              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                {selectedProspect.ats_matched_keywords.map((kw, i) => (<Chip key={i} label={kw} size="small" color="success" variant="outlined" sx={{ height: 26 }} />))}
                              </Stack>
                            </Box>
                          )}
                          {selectedProspect.ats_missing_keywords?.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{t('prospect_missing_keywords')}</Typography>
                              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                {selectedProspect.ats_missing_keywords.slice(0, 15).map((kw, i) => (<Chip key={i} label={kw} size="small" color="error" variant="outlined" sx={{ height: 26 }} />))}
                                {selectedProspect.ats_missing_keywords.length > 15 && (<Chip label={`+${selectedProspect.ats_missing_keywords.length - 15}`} size="small" variant="outlined" sx={{ height: 26 }} />)}
                              </Stack>
                            </Box>
                          )}
                          {selectedProspect.cover_letter && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{t('prospect_cover_letter')}</Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', bgcolor: 'grey.50', p: 2, borderRadius: 1.5, border: 1, borderColor: 'divider' }}>{selectedProspect.cover_letter}</Typography>
                            </Box>
                          )}
                          <Box sx={{ mb: 2 }}>
                            <Button variant="outlined" size="small" startIcon={<OpenInNewIcon />} onClick={() => setResumePreviewOpen(true)} sx={{ textTransform: 'none' }}>{t('prospect_resume')}</Button>
                          </Box>
                          <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', gap: 2 }}>
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                              <InputLabel>{t('prospect_status')}</InputLabel>
                              <Select value={selectedProspect.status || 'new'} onChange={(e) => handleUpdateProspectStatus(selectedProspect._id, e.target.value)} label={t('prospect_status')}>
                                <MenuItem value="new">{t('prospect_status_new')}</MenuItem>
                                <MenuItem value="reviewed">{t('prospect_status_reviewed')}</MenuItem>
                                <MenuItem value="rejected">{t('prospect_status_rejected')}</MenuItem>
                              </Select>
                            </FormControl>
                            {!selectedProspect.promoted_to_candidate ? (
                              <Button variant="contained" color="primary" startIcon={promotingId === selectedProspect._id ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />} onClick={() => handlePromoteProspect(selectedProspect._id)} disabled={promotingId !== null} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } }}>
                                {promotingId === selectedProspect._id ? t('please_wait') : t('prospect_promote')}
                              </Button>
                            ) : (
                              <Chip icon={<CheckCircleIcon />} label={t('prospect_status_promoted')} color="success" sx={{ height: 32 }} />
                            )}
                          </Stack>
                        </Paper>
                      )}
                    </Paper>
                  </Grid>
                )}

                {/* --- DESKTOP: Prospects bubble after the row --- */}
                {!isMobile && isBubbleRow && bubbleIndex !== -1 && (
                  <Grid item xs={12} sx={{ position: 'relative' }} ref={detailsRef}>
                    <Paper elevation={0} sx={{ position: 'relative', border: 2, borderColor: 'primary.main', borderRadius: 2, p: { xs: 2, sm: 3 }, mb: 2, mt: 0, zIndex: 10, minWidth: 280 }}>
                      <IconButton size="small" sx={{ position: 'absolute', top: 8, right: 8 }} onClick={() => { setSelectedVacancy(null); setProspects([]); setSelectedProspect(null); }}>
                        <CloseIcon />
                      </IconButton>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                          <PeopleIcon sx={{ fontSize: 20 }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {t('vacancy_prospects')} — {selectedVacancy.title}
                        </Typography>
                      </Box>

                      {loadingProspects && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <CircularProgress size={28} />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading prospects...</Typography>
                        </Box>
                      )}

                      {!loadingProspects && prospects.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">{t('vacancy_no_prospects')}</Typography>
                        </Box>
                      )}

                      {!loadingProspects && prospects.length > 0 && (
                        <Grid container spacing={2}>
                          {prospects.map((prospect) => (
                            <Grid item xs={12} sm={6} md={4} key={prospect._id}>
                              <Paper
                                elevation={0}
                                sx={{
                                  cursor: 'pointer', borderRadius: 2,
                                  border: selectedProspect?._id === prospect._id ? '2px solid' : '1px solid',
                                  borderColor: selectedProspect?._id === prospect._id ? 'secondary.main' : 'divider',
                                  transition: 'all 0.15s ease-in-out',
                                  '&:hover': { borderColor: 'secondary.light', boxShadow: 1 }, minHeight: 120
                                }}
                                onClick={() => setSelectedProspect(prospect)}
                              >
                                <Box sx={{ p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{prospect.fullname}</Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{prospect.email}</Typography>
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 'auto' }}>
                                    {getProspectStatusChip(prospect.status)}
                                    <Typography variant="caption" color="text.secondary">{t('prospect_ats_score')}: {prospect.ats_score}%</Typography>
                                  </Stack>
                                </Box>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      )}

                      {/* Prospect Detail (inline) */}
                      {selectedProspect && (
                        <Paper elevation={0} sx={{ mt: 3, p: { xs: 2, sm: 3 }, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>{selectedProspect.fullname}</Typography>
                              <Typography variant="body2" color="text.secondary">{selectedProspect.email}</Typography>
                            </Box>
                            <IconButton size="small" onClick={() => setSelectedProspect(null)}><CloseIcon /></IconButton>
                          </Box>
                          <Box sx={{ mt: 2, mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{t('prospect_ats_score')}</Typography>
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <LinearProgress variant="determinate" value={selectedProspect.ats_score || 0} sx={{ flex: 1, height: 10, borderRadius: 5 }} color={selectedProspect.ats_score >= 50 ? 'success' : selectedProspect.ats_score >= 25 ? 'warning' : 'error'} />
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedProspect.ats_score || 0}%</Typography>
                            </Stack>
                          </Box>
                          {selectedProspect.ats_matched_keywords?.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{t('prospect_matched_keywords')}</Typography>
                              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                {selectedProspect.ats_matched_keywords.map((kw, i) => (<Chip key={i} label={kw} size="small" color="success" variant="outlined" sx={{ height: 26 }} />))}
                              </Stack>
                            </Box>
                          )}
                          {selectedProspect.ats_missing_keywords?.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{t('prospect_missing_keywords')}</Typography>
                              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                {selectedProspect.ats_missing_keywords.slice(0, 15).map((kw, i) => (<Chip key={i} label={kw} size="small" color="error" variant="outlined" sx={{ height: 26 }} />))}
                                {selectedProspect.ats_missing_keywords.length > 15 && (<Chip label={`+${selectedProspect.ats_missing_keywords.length - 15}`} size="small" variant="outlined" sx={{ height: 26 }} />)}
                              </Stack>
                            </Box>
                          )}
                          {selectedProspect.cover_letter && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{t('prospect_cover_letter')}</Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', bgcolor: 'grey.50', p: 2, borderRadius: 1.5, border: 1, borderColor: 'divider' }}>{selectedProspect.cover_letter}</Typography>
                            </Box>
                          )}
                          <Box sx={{ mb: 2 }}>
                            <Button variant="outlined" size="small" startIcon={<OpenInNewIcon />} onClick={() => setResumePreviewOpen(true)} sx={{ textTransform: 'none' }}>{t('prospect_resume')}</Button>
                          </Box>
                          <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', gap: 2 }}>
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                              <InputLabel>{t('prospect_status')}</InputLabel>
                              <Select value={selectedProspect.status || 'new'} onChange={(e) => handleUpdateProspectStatus(selectedProspect._id, e.target.value)} label={t('prospect_status')}>
                                <MenuItem value="new">{t('prospect_status_new')}</MenuItem>
                                <MenuItem value="reviewed">{t('prospect_status_reviewed')}</MenuItem>
                                <MenuItem value="rejected">{t('prospect_status_rejected')}</MenuItem>
                              </Select>
                            </FormControl>
                            {!selectedProspect.promoted_to_candidate ? (
                              <Button variant="contained" color="primary" startIcon={promotingId === selectedProspect._id ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />} onClick={() => handlePromoteProspect(selectedProspect._id)} disabled={promotingId !== null} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } }}>
                                {promotingId === selectedProspect._id ? t('please_wait') : t('prospect_promote')}
                              </Button>
                            ) : (
                              <Chip icon={<CheckCircleIcon />} label={t('prospect_status_promoted')} color="success" sx={{ height: 32 }} />
                            )}
                          </Stack>
                        </Paper>
                      )}
                    </Paper>
                    <div className="horizontal-connector" />
                  </Grid>
                )}
              </React.Fragment>
            );
          })}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openFormDialog} onClose={() => setOpenFormDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {editingVacancy ? t('vacancy_edit') : t('vacancy_create')}
          </Typography>
          <IconButton onClick={() => setOpenFormDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label={t('vacancy_title')}
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              fullWidth
              required
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
            <TextField
              label={t('vacancy_description')}
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              fullWidth
              multiline
              minRows={4}
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
            <FormControl fullWidth required size="small">
              <InputLabel>{t('vacancy_position')}</InputLabel>
              <Select
                value={formPosition}
                onChange={(e) => setFormPosition(e.target.value)}
                label={t('vacancy_position')}
              >
                {positions.map((position) => (
                  <MenuItem key={position.id} value={position.id}>{position.display_text}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>{t('vacancy_status')}</InputLabel>
              <Select
                value={formStatus}
                onChange={(e) => setFormStatus(e.target.value)}
                label={t('vacancy_status')}
              >
                <MenuItem value="open">{t('vacancy_status_open')}</MenuItem>
                <MenuItem value="closed">{t('vacancy_status_closed')}</MenuItem>
              </Select>
            </FormControl>

            {/* ATS Keywords Section */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t('ats_keywords_label')}</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSuggestKeywords}
                  disabled={suggestingKeywords || !formDescription.trim()}
                  startIcon={suggestingKeywords ? <CircularProgress size={14} /> : null}
                  sx={{ textTransform: 'none' }}
                >
                  {t('ats_suggest_keywords')}
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('ats_keywords_hint')}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1 }}>
                {formAtsKeywords.map((kw) => (
                  <Chip
                    key={kw}
                    label={kw}
                    onDelete={() => handleRemoveKeyword(kw)}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 26 }}
                  />
                ))}
                {formAtsKeywords.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {t('ats_no_keywords') || 'No keywords selected. Click "Suggest Keywords" or add manually below.'}
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  placeholder={t('ats_add_keyword')}
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddKeyword(); } }}
                  sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
                <Button size="small" variant="outlined" onClick={handleAddKeyword} disabled={!newKeyword.trim()} sx={{ minWidth: 40 }}>
                  <AddIcon fontSize="small" />
                </Button>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setOpenFormDialog(false)} sx={{ textTransform: 'none' }}>
            {t('cancel')}
          </Button>
          <LoadingButton
            onClick={handleSaveVacancy}
            variant="contained"
            disabled={!formTitle.trim() || formDescription.trim().length < 10 || !formPosition}
            loading={savingVacancy}
            loadingLabel={t('please_wait')}
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {t('save')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('vacancy_confirm_delete_title')}
          </Typography>
          <IconButton onClick={() => setDeleteConfirm(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">{t('vacancy_confirm_delete')}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setDeleteConfirm(null)} sx={{ textTransform: 'none' }}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleDeleteVacancy}
            color="error"
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resume Preview Dialog */}
      {resumePreviewOpen && selectedProspect && (
        <Dialog open={true} onClose={() => setResumePreviewOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('prospect_resume')}</Typography>
              <Typography variant="caption" color="text.secondary">{selectedProspect.fullname}</Typography>
            </Box>
            <IconButton onClick={() => setResumePreviewOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, bgcolor: 'grey.50' }}>
            {selectedProspect.resume_filename?.toLowerCase().endsWith('.pdf') ? (
              <iframe
                src={`${API_ENDPOINTS.PROSPECT_RESUME(selectedProspect._id)}?preview=true`}
                title="Resume Preview"
                width="100%"
                height="500px"
                style={{ border: 'none' }}
              />
            ) : (
              <img
                src={`${API_ENDPOINTS.PROSPECT_RESUME(selectedProspect._id)}?preview=true`}
                alt={selectedProspect.resume_filename}
                style={{ maxWidth: '100%', maxHeight: 500, display: 'block', margin: '0 auto' }}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={() => setResumePreviewOpen(false)} sx={{ textTransform: 'none' }}>
              {t('close') || 'Close'}
            </Button>
            <Button
              onClick={() => window.open(`${API_ENDPOINTS.PROSPECT_RESUME(selectedProspect._id)}`, '_blank')}
              variant="contained"
              startIcon={<OpenInNewIcon />}
              sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
            >
              {t('download')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <FixedSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}

export default Vacancies;
